import React from 'react';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { bgColorClassname } from '@/utils';
import { RichText, Heading, Highlight, Image, Link, MaskedImage } from '@/components';
import { LinkInterface } from '@/types';

// Define interfaces for components used within TextBlock
interface ImageWithAlt {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
}

interface ImageWithMask extends ImageWithAlt {
  mask: string;
}

interface Section {
  title?: string;
  content: PortableTextBlockType;
  includeCtas: boolean;
  image?: ImageWithAlt;
  ctas?: LinkInterface[];
}

interface CalloutData {
  style: 'coral' | 'royalBlue';
  text: string;
}

interface TextBlockProps {
  // Props for configuring different aspects of the text block
  backgroundColor?: {
    label: string;
    value: string;
  };
  title?: string;
  content?: PortableTextBlockType;
  includeCta: boolean;
  cta?: LinkInterface | undefined;
  includeCallout: boolean;
  callout?: CalloutData;
  includeCtas: boolean;
  ctas?: LinkInterface[] | undefined;
  showMap: boolean;
  mapEmbedUrl?: string;
  image?: ImageWithMask;
  imageLayout?: string;
  sections?: Section[];
  textAlignCenter?: boolean;
  blurbClassName?: string;
  layoutWidth?: string;
}

const getGridColsClass = (layoutWidth: string | undefined, sectionsLength: number): string => {
  switch (layoutWidth) {
    case '1/3':
      return sectionsLength === 1 ? 'lg:grid-cols-1' : 'lg:grid-cols-1 xl:grid-cols-1';
    case '1/2':
      return sectionsLength === 1 ? 'lg:grid-cols-1' : 'lg:grid-cols-2';
    case '2/3':
      return sectionsLength === 2
        ? 'lg:grid-cols-1 xl:grid-cols-2'
        : sectionsLength === 3
          ? 'lg:grid-cols-2 xl:grid-cols-3'
          : '';
    case undefined:
      return sectionsLength === 2
        ? 'sm:grid-cols-2 sm:gap-x-5'
        : sectionsLength === 3
          ? 'sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-5'
          : '';
    default:
      return '';
  }
};

const TextBlock: React.FC<TextBlockProps> = ({
  backgroundColor,
  title,
  content,
  includeCta,
  cta,
  includeCallout,
  callout,
  includeCtas,
  ctas,
  showMap,
  mapEmbedUrl,
  image,
  imageLayout,
  sections,
  textAlignCenter,
  blurbClassName,
  layoutWidth,
}) => {
  const ImageComponent = () => {
    const hasImage = image && image.image;
    const hasMask = image && image.mask && image.mask !== 'no-mask';

    return hasImage ? (
      <div
        className={
          imageLayout && imageLayout === 'imageBesideContent'
            ? 'order-none	sm:order-2 md:order-none lg:order-2'
            : 'pb-8 pt-3'
        }
      >
        {hasMask ? (
          <MaskedImage
            image={image.image}
            mask={image.mask}
            className='aspect-square w-full'
            alt={image.alt || title || ''}
            width={350}
          />
        ) : (
          <Image
            className='w-full rounded-[20px]'
            image={image.image}
            alt={image.alt || title || ''}
            width={350}
          />
        )}
      </div>
    ) : null;
  };

  const Content = () =>
    content && (
      <RichText
        className={`${textAlignCenter ? 'w-full max-w-[770px] gap-8 text-center' : ''} ${blurbClassName || ''}`}
        value={content}
      />
    );

  const Ctas = ({ className = '' }: { className?: string }) => {
    if (!includeCtas || !ctas?.length) return null;

    return (
      <div className={className}>
        {ctas.map((cta, index) => (
          <Link
            key={index}
            {...cta}
            variant={cta.type === 'primary' ? 'default' : 'outline'}
            size='sm'
          >
            {cta.label}
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='font-halyarddisplay mx-auto max-w-screen-xl px-5 py-8 md:flex md:gap-x-6 md:p-[50px] lg:gap-x-12 xl:gap-x-24'>
        <div
          className={`w-full text-lg font-normal xl:text-xl ${textAlignCenter ? 'flex flex-col items-center' : ''}`}
        >
          {/* Render title if provided */}
          {title && (
            <Heading
              title={title}
              isShowButton={includeCta}
              cta={cta}
              className={`${showMap ? 'mb-8' : textAlignCenter ? 'text-center' : 'mb-5'}`}
              isShowCurveLine={!textAlignCenter}
            />
          )}
          {imageLayout && imageLayout === 'imageBesideContent' ? (
            <div className='grid grid-cols-1 items-center gap-6 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2'>
              <div className='order-2	sm:order-none md:order-2 lg:order-none'>
                <Content />
                <Ctas className='flex w-full flex-1 flex-wrap gap-4 pt-4' />
              </div>
              <ImageComponent />
            </div>
          ) : (
            <>
              <ImageComponent />
              <Content />
            </>
          )}
          {/* Render embedded map if showMap is true */}
          {showMap && (
            <div className='relative py-9'>
              <div className='relative h-0 overflow-hidden pb-[66.67%]'>
                <iframe
                  src={mapEmbedUrl}
                  width='720'
                  height='480'
                  loading='lazy'
                  title="National Children's Museum"
                  className='absolute left-0 top-0 h-full w-full border-0'
                  style={{
                    aspectRatio: '3 / 2',
                  }}
                ></iframe>
              </div>
            </div>
          )}
          {/* Render sections if provided */}
          {sections && sections.length > 0 && (
            <div
              className={`grid grid-cols-1 ${getGridColsClass(layoutWidth, sections.length)} gap-y-5 md:gap-x-9`}
            >
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {section?.image && (
                    <div className='mb-5 flex justify-center'>
                      <Image
                        image={section?.image?.image}
                        alt={section?.image?.alt || section.title}
                        className='rounded-xl'
                        width={400}
                      />
                    </div>
                  )}
                  {/* Render section title if provided */}
                  {section.title && (
                    <h3 className='pb-2 text-2xl font-medium leading-7'>{section.title}</h3>
                  )}
                  {/* Render section content */}
                  <RichText
                    as='p'
                    className='text-base font-light leading-[20px]'
                    value={section.content}
                  />
                  {section.includeCtas && section.ctas && section.ctas.length > 0 && (
                    <div className='mt-6 flex flex-col items-center justify-center gap-4'>
                      {section.ctas.map((cta, ctaIndex) => {
                        const bgColor = backgroundColor ? backgroundColor.value : '';
                        const isBgColorLight = [
                          '#FFE23D',
                          '#FFD700',
                          '#DAF1E2',
                          '#C9EDF3',
                          '#FC9997',
                          '#ACE4EC',
                        ].includes(bgColor || '');

                        return (
                          <Link
                            key={ctaIndex}
                            {...cta}
                            variant={
                              cta.type === 'primary'
                                ? isBgColorLight
                                  ? 'outline'
                                  : 'default'
                                : 'linkInline'
                            }
                            size={cta.type === 'primary' ? 'sm' : null}
                            className={cta.type === 'primary' ? 'w-full' : ''}
                          >
                            {cta.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Render callout if includeCallout is true */}
          {includeCallout && callout && (
            <Highlight
              highlightText={callout.text}
              icon={callout.style}
            />
          )}
          {/* Render multiple CTAs if includeCtas is true */}
          {includeCtas &&
            ctas &&
            ctas?.length > 0 &&
            (!imageLayout || imageLayout !== 'imageBesideContent') && (
              <Ctas
                className={`mt-8 flex w-full flex-1 flex-wrap gap-4 ${textAlignCenter && 'justify-center'}`}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default TextBlock;
